/**
 * Application Routes
 * Defines all routes with lazy loading and role-based access control
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading } from '../components/common';
import ProtectedRoute from './ProtectedRoute';
import RoleBasedRoute from './RoleBasedRoute';
import { ROUTES } from './routeConfig';

// Lazy load pages for better performance
// Public pages
const HomePage = lazy(() => import('../pages/public/HomePage'));

// Auth pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));

// Student pages
const StudentDashboardPage = lazy(() => import('../pages/student/StudentDashboardPage'));
const StudentExamsPage = lazy(() => import('../pages/student/ExamsPage'));
const StudentExamDetailPage = lazy(() => import('../pages/student/ExamDetailPage'));
const TakeExamPage = lazy(() => import('../pages/student/TakeExamPage'));
const StudentResultsPage = lazy(() => import('../pages/student/ResultsPage'));
const StudentResultDetailPage = lazy(() => import('../pages/student/ResultDetailPage'));
const StudentProgressPage = lazy(() => import('../pages/student/ProgressPage'));
const StudentProfilePage = lazy(() => import('../pages/student/ProfilePage'));
const StudentSettingsPage = lazy(() => import('../pages/student/SettingsPage'));

// Teacher pages
const TeacherDashboardPage = lazy(() => import('../pages/teacher/TeacherDashboardPage'));
const TeacherExamsPage = lazy(() => import('../pages/teacher/ExamsPage'));
const TeacherExamCreatePage = lazy(() => import('../pages/teacher/ExamCreatePage'));
const TeacherExamEditPage = lazy(() => import('../pages/teacher/ExamEditPage'));
const TeacherQuestionsPage = lazy(() => import('../pages/teacher/QuestionsPage'));
const TeacherClassesPage = lazy(() => import('../pages/teacher/ClassesPage'));
const TeacherClassDetailPage = lazy(() => import('../pages/teacher/ClassDetailPage'));
const TeacherGradingPage = lazy(() => import('../pages/teacher/GradingPage'));
const TeacherAnalyticsPage = lazy(() => import('../pages/teacher/AnalyticsPage'));
const TeacherExamAnalyticsPage = lazy(() => import('../pages/teacher/ExamAnalyticsPage'));
const TeacherProfilePage = lazy(() => import('../pages/teacher/ProfilePage'));
const TeacherSettingsPage = lazy(() => import('../pages/teacher/SettingsPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('../pages/admin/UsersPage'));
const AdminExamsPage = lazy(() => import('../pages/admin/ExamsPage'));
const AdminOrganizationsPage = lazy(() => import('../pages/admin/OrganizationsPage'));
const AdminSchoolsPage = lazy(() => import('../pages/admin/SchoolsPage'));
const AdminSchoolDetailPage = lazy(() => import('../pages/admin/SchoolDetailPage'));
const AdminSaaSDataPage = lazy(() => import('../pages/admin/SaaSDataPage'));
const AdminSecurityPage = lazy(() => import('../pages/admin/SecurityPage'));
const AdminSettingsPage = lazy(() => import('../pages/admin/AdminSettingsPage'));
const AdminStudentProfileView = lazy(() => import('../pages/admin/StudentProfileView'));
const AdminTeacherProfileView = lazy(() => import('../pages/admin/TeacherProfileView'));

// Staff pages
const StaffDashboardPage = lazy(() => import('../pages/staff/StaffDashboardPage'));
const StaffTeachersPage = lazy(() => import('../pages/staff/StaffTeachersPage'));
const StaffStudentsPage = lazy(() => import('../pages/staff/StaffStudentsPage'));
const StaffClassesPage = lazy(() => import('../pages/staff/StaffClassesPage'));
const StaffSettingsPage = lazy(() => import('../pages/staff/StaffSettingsPage'));

// Parent pages
const ParentDashboardPage = lazy(() => import('../pages/parent/ParentDashboardPage'));
const ParentSettingsPage = lazy(() => import('../pages/parent/ParentSettingsPage'));

// Teacher settings (new)
const TeacherSettingsPageNew = lazy(() => import('../pages/teacher/TeacherSettingsPage'));

// Student settings (new)
const StudentSettingsPageNew = lazy(() => import('../pages/student/StudentSettingsPage'));

// Editor pages
const QuestionBankEditorPage = lazy(() => import('../pages/editor/QuestionBankEditorPage'));
const SLPEditorPage = lazy(() => import('../pages/editor/SLPEditorPage'));

// SLP pages (from MyClass)
const CalendarPage = lazy(() => import('../components/Calendar/CalendarPage'));
const ClassInfoPage = lazy(() => import('../components/ClassInfo/ClassInfoPage'));
const ECAPage = lazy(() => import('../components/ECA/ECAPage'));
const SLPManagementPage = lazy(() => import('../components/SLP/SLPManagementPage'));
const StudentInfoPage = lazy(() => import('../components/StudentInfo/StudentInfoPage'));

// Error pages
const NotFoundPage = lazy(() => import('../pages/errors/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('../pages/errors/UnauthorizedPage'));
const ForbiddenPage = lazy(() => import('../pages/errors/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('../pages/errors/ServerErrorPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-exam-surface">
    <Loading size="lg" text="Loading..." />
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        
        {/* Auth Routes */}
        <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.AUTH.REGISTER} element={<RegisterPage />} />
        <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        
        {/* Student Routes */}
        <Route
          path={ROUTES.STUDENT.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAM_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentExamDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.TAKE_EXAM}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <TakeExamPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULTS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentResultsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULT_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentResultDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROGRESS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentProgressPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentProfilePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentSettingsPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Teacher Routes */}
        <Route
          path={ROUTES.TEACHER.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_CREATE}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamCreatePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_EDIT}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamEditPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.QUESTIONS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherQuestionsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASSES}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherClassesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASS_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherClassDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.GRADING}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherGradingPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherAnalyticsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamAnalyticsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherProfilePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherSettingsPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path={ROUTES.ADMIN.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.USERS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUsersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.ORGANIZATIONS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminOrganizationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SECURITY}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSecurityPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SCHOOLS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSchoolsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SCHOOL_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSchoolDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SAAS_DATA}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSaaSDataPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.STUDENT_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'staff']}>
              <AdminStudentProfileView />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.TEACHER_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'staff']}>
              <AdminTeacherProfileView />
            </RoleBasedRoute>
          }
        />
        
        {/* Staff Routes */}
        <Route
          path={ROUTES.STAFF.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.TEACHERS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffTeachersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.STUDENTS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffStudentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.CLASSES}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffClassesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.STUDENT_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <AdminStudentProfileView />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.TEACHER_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <AdminTeacherProfileView />
            </RoleBasedRoute>
          }
        />
        
        {/* Editor Routes - Admin and Teacher */}
        <Route
          path={ROUTES.EDITOR.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <QuestionBankEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TABLE}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <QuestionBankEditorPage />
            </RoleBasedRoute>
          }
        />
        
        {/* SLP Editor Routes - Admin, Teacher, and Staff */}
        <Route
          path={ROUTES.SLP_EDITOR.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SLPEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.SLP_EDITOR.TABLE}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SLPEditorPage />
            </RoleBasedRoute>
          }
        />
        
        {/* SLP Routes - Students, Teachers, Staff, Admin */}
        <Route
          path={ROUTES.SLP.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'teacher', 'staff', 'admin']}>
              <SLPManagementPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Calendar Routes */}
        <Route
          path={ROUTES.CALENDAR.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'teacher', 'staff', 'admin']}>
              <CalendarPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Class Info Routes */}
        <Route
          path={ROUTES.CLASS_INFO.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'teacher', 'staff', 'admin']}>
              <ClassInfoPage />
            </RoleBasedRoute>
          }
        />
        
        {/* ECA Routes */}
        <Route
          path={ROUTES.ECA.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'teacher', 'staff', 'admin']}>
              <ECAPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Student Info Routes */}
        <Route
          path={ROUTES.STUDENT_INFO.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'teacher', 'staff', 'admin']}>
              <StudentInfoPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Parent Routes */}
        <Route
          path={ROUTES.PARENT.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['parent', 'admin']}>
              <ParentDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.PARENT.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['parent', 'admin']}>
              <ParentSettingsPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Error Routes */}
        <Route path={ROUTES.ERRORS.UNAUTHORIZED} element={<UnauthorizedPage />} />
        <Route path={ROUTES.ERRORS.FORBIDDEN} element={<ForbiddenPage />} />
        <Route path={ROUTES.ERRORS.SERVER_ERROR} element={<ServerErrorPage />} />
        <Route path={ROUTES.ERRORS.NOT_FOUND} element={<NotFoundPage />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to={ROUTES.ERRORS.NOT_FOUND} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
